import { Col, Row, Tooltip } from "antd";
import React, { FC, useState } from "react";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";

import { ButtonWithIcon } from "../../atoms/Button";
import { Radio } from "../../atoms/Radio";
import { Select } from "../../atoms/Select";
import { Tab } from "../../atoms/Tab";
import { TextArea } from "../../atoms/TextArea";
import { TextInput } from "../../atoms/TextInput";
import { DatePickerWithLabel } from "../../molecules/DatePickerWithLabel";
import { InputWithLabel } from "../../molecules/InputWithLabel";
import { RadioWithLabel } from "../../molecules/RadioWithLabel";
import { SelectWithLabel } from "../../molecules/SelectWithLabel";
import {
  CharRefRow,
  CharRefTitle,
  DetailsWrapper,
  LogsWrapper,
  ParagraphTitle,
  ResumeButtonsWrapper,
  SkillsName,
  SkillsNameCol,
  SkillsRow,
  TooltipStyled,
} from "./elements";
import { Logs } from "../Logs";
import { Log } from "../../../core/entities/log";
import { DocumentViewerPopup } from "../DocumentViewerPopup";
import { sortSkills } from "../../../utils/helpers/transform";

type Props = {
  applicantDetails: {
    applicantName: {
      firstName: string;
      middleName?: string;
      lastName: string;
    };
    address: string;
    agileExperience: string;
    birthDate: Date;
    characterReference: Array<{
      contactNumber: string;
      name: string;
      relationship: string;
    }>;
    civilStatus: string;
    contactNumber: string;
    contactTimeFrom: string;
    portfolio: string;
    contactTimeTo: string;
    course: string;
    dateCompleted: Date;
    degree: string;
    email: string;
    expectedSalary: number;
    fullName: string;
    linkedinUrl: string;
    platformApplied: string;
    positionApplied: string;
    reasonOfLeaving: string;
    recentWork: string;
    referredBy: string;
    resume: string;
    school: string;
    skills: object;
    toolsUsed: string;
    willingToRelocate: string;
    workOffice: string;
    yearsOfExperience: string;
    logs: {
      id: string;
      logs: Log[];
    };
  };
  openResume: () => void;
  downloadDocument: (fileName: string) => void;
  isInterviewer?: boolean;
  isDownloadingResume?: boolean;
};

const ApplicantDetailsTabs: FC<Props> = ({
  applicantDetails,
  openResume,
  downloadDocument,
  isInterviewer,
  isDownloadingResume,
}) => {
  const [showPopupResume, setShowPopupResume] = useState<boolean>(false);
  const [loadResume, setLoadResume] = useState(true);

  const handleResumeLoad = (load: boolean) => {
    setLoadResume(load);
  };

  const radioItems: JSX.Element = (
    <>
      <Radio radioValue={"Yes"}>Yes</Radio>
      <Radio radioValue={"No"}>No</Radio>
    </>
  );

  const tabs = [
    {
      title: "Personal",
      content: (
        <DetailsWrapper>
          <InputWithLabel
            label="First Name"
            type="default"
            value={applicantDetails.applicantName.firstName}
            disabled={true}
          />
          <Row gutter={[16, 0]}>
            <Col xl={12}>
              <InputWithLabel
                  label="Middle Name"
                  type="default"
                  value={applicantDetails.applicantName.middleName}
                  disabled={true}
                />
            </Col>
            <Col xl={12}>
              <InputWithLabel
                label="Last Name"
                type="default"
                value={applicantDetails.applicantName.lastName}
                disabled={true}
              />
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xl={isInterviewer ? 24 : 12}>
              <SelectWithLabel
                label="Position Applied"
                selectDefaultValue={applicantDetails.positionApplied}
                disabled={true}
              />
            </Col>
            {!isInterviewer && (
              <Col xl={12}>
                <InputWithLabel
                  label="Expected Salary"
                  type="currency"
                  value={applicantDetails.expectedSalary}
                  disabled={true}
                />
              </Col>
            )}
          </Row>
          <Row gutter={[16, 0]}>
            <Col xl={12}>
              <DatePickerWithLabel
                label="Date of Birth"
                disabled={true}
                value={moment(new Date(applicantDetails.birthDate))}
              />
            </Col>
            <Col xl={12}>
              <SelectWithLabel
                label="Civil Status"
                selectDefaultValue={applicantDetails.civilStatus}
                disabled={true}
              />
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xl={12}>
              <SelectWithLabel
                label="Educational Attainment"
                selectDefaultValue={applicantDetails.degree}
                disabled={true}
              />
            </Col>
            <Col xl={12}>
              <DatePickerWithLabel
                label="Year & Month Graduated"
                disabled={true}
                value={moment(new Date(applicantDetails.dateCompleted))}
              />
            </Col>
          </Row>
          <InputWithLabel
            label="Name of School"
            type="default"
            value={applicantDetails.school}
            disabled={true}
          />
          <InputWithLabel
            label="Course"
            type="default"
            value={applicantDetails.course}
            disabled={true}
          />
          <InputWithLabel
            label="Do you have any sample work or portfolio? (Optional)"
            type="default"
            value={applicantDetails.portfolio}
            disabled={true}
          />
          <InputWithLabel
            label="Referred By (Optional)"
            type="default"
            value={applicantDetails.referredBy}
            disabled={true}
          />
        </DetailsWrapper>
      ),
    },
    {
      title: "Knowledge and Skills",
      content: (
        <DetailsWrapper>
          {sortSkills(applicantDetails.skills).map((item: any) => (
            <SkillsRow gutter={[16, 0]} key={item.skill}>
              <SkillsNameCol sm={24} xl={10}>
                <SkillsName>{item.skill}</SkillsName>
              </SkillsNameCol>
              <Col sm={24} xl={14}>
                <TooltipStyled>
                  <Tooltip
                    placement={"bottom"}
                    title={item.value}
                    getPopupContainer={triggerNode => triggerNode}
                  >
                    <Select defaultValue={item.value} disabled={true} />
                  </Tooltip>
                </TooltipStyled>
              </Col>
            </SkillsRow>
          ))}
        </DetailsWrapper>
      ),
    },
    {
      title: "Contact",
      content: (
        <DetailsWrapper>
          <InputWithLabel
            label="LinkedIn URL (optional)"
            type="default"
            value={applicantDetails.linkedinUrl}
            disabled={true}
          />
          <Row gutter={[16, 0]}>
            <Col xl={12}>
              <InputWithLabel
                label="Email Address"
                type="default"
                value={applicantDetails.email}
                disabled={true}
              />
            </Col>
            <Col xl={12}>
              <InputWithLabel
                label="Contact Number"
                type="default"
                value={applicantDetails.contactNumber}
                disabled={true}
              />
            </Col>
          </Row>
          <Row gutter={[16, 0]}>
            <Col xl={12}>
              <SelectWithLabel
                label="Best Time To Contact"
                selectDefaultValue={applicantDetails.contactTimeFrom}
                disabled={true}
              />
            </Col>
            <Col xl={12} style={{ paddingTop: "42px" }}>
              <Select
                defaultValue={applicantDetails.contactTimeTo}
                disabled={true}
              />
            </Col>
          </Row>
          <InputWithLabel
            label="Present Address"
            type="default"
            value={applicantDetails.address}
            disabled={true}
          />
          {applicantDetails &&
            applicantDetails.characterReference &&
            applicantDetails.characterReference.length !== 0 && (
              <CharRefTitle>
                <span>Character References</span>
                <ul style={{ paddingLeft: "15px" }}>
                  <li>atleast 3 skills required</li>
                  <li>preferably immediate head/superior from previous work</li>
                  <li> write N/A for fresh graduates</li>
                </ul>
              </CharRefTitle>
            )}
          {applicantDetails &&
            applicantDetails.characterReference &&
            applicantDetails.characterReference.map(
              (reference: any, index: number) => (
                <CharRefRow gutter={[16, 16]} key={index}>
                  <Col xl={12}>
                    <TextInput value={reference.name} disabled={true} />
                  </Col>
                  <Col xl={12}>
                    <TextInput
                      value={reference.contactNumber}
                      disabled={true}
                    />
                  </Col>
                  <Col xl={24}>
                    <TextInput value={reference.emailCharRef} disabled={true} />
                  </Col>
                  <Col xl={24}>
                    <TextInput value={reference.relationship} disabled={true} />
                  </Col>
                </CharRefRow>
              )
            )}
        </DetailsWrapper>
      ),
    },
    {
      title: "Pre-Screening",
      content: (
        <DetailsWrapper>
          <RadioWithLabel
            label="This position may become an office-based job role. If currently living outside Cebu City, are you willing to relocate at your own expense once improvements allow full office-based operations?"
            disabled={true}
            radioItems={
              <>
                <Radio radioValue={"Yes"}>Yes</Radio>
                <Radio radioValue={"No"}>No</Radio>
                <Radio radioValue={"Not Applicable"}>Not Applicable</Radio>
              </>
            }
            value={applicantDetails.willingToRelocate}
          />
          <SelectWithLabel
            label="Indicate your years of experience for position applied"
            selectDefaultValue={applicantDetails.yearsOfExperience}
            disabled={true}
          />
          <ParagraphTitle>
            Summary of recent work experiences (Indicate company name, inclusive
            dates, job title, scope of work, daily routine)
          </ParagraphTitle>
          <TextArea value={applicantDetails.recentWork} disabled={true} />
          <InputWithLabel
            label="Reason for leaving previous job"
            type="default"
            value={applicantDetails.reasonOfLeaving}
            disabled={true}
          />
          <ParagraphTitle>
            Tools you used in carrying out your job
          </ParagraphTitle>
          <TextArea value={applicantDetails.toolsUsed} disabled={true} />
          <RadioWithLabel
            label="Do you have experience working with scrum agile team?"
            disabled={true}
            radioItems={radioItems}
            value={applicantDetails.agileExperience}
          />
          <RadioWithLabel
            label="Are you willing to work in the office once everything goes back to normal?"
            disabled={true}
            radioItems={radioItems}
            value={applicantDetails.workOffice}
          />
          <RadioWithLabel
            label="Which platform did you use to send your resume?"
            disabled={true}
            radioItems={
              <>
                <Radio radioValue={"Facebook"}>Facebook</Radio>
                <br />
                <Radio radioValue={"Mynimo"}>Mynimo</Radio>
                <br />
                <Radio radioValue={"Jobstreet"}>Jobstreet</Radio>
                <br />
                <Radio radioValue={"LinkedIn"}>LinkedIn</Radio>
              </>
            }
            value={applicantDetails.platformApplied}
          />
          <ParagraphTitle>Upload updated resume</ParagraphTitle>
          <ResumeButtonsWrapper>
            <ButtonWithIcon
              icon={<SearchOutlined />}
              loading={loadResume}
              disabled={loadResume}
              onClick={() => setShowPopupResume(true)}
            >
              { loadResume ? 'Loading Resume' : 'View Resume'}
            </ButtonWithIcon>
            <ButtonWithIcon
              disabled={isDownloadingResume}
              $isPrimary={true}
              icon={<DownloadOutlined />}
              onClick={() => { downloadDocument(applicantDetails.resume) }}
            >
              { isDownloadingResume ? 'Downloading Resume' : 'Download Resume' }  
            </ButtonWithIcon>
          </ResumeButtonsWrapper>
        </DetailsWrapper>
      ),
    },
    {
      title: "Logs",
      content: (
        <LogsWrapper>
          <Logs logs={applicantDetails.logs} />
        </LogsWrapper>
      ),
    },
  ];

  return (
    <>
    <Tab tabs={tabs} />
    <DocumentViewerPopup
      isOpen={showPopupResume}
      src={applicantDetails.resume}
      onLoadDocument={handleResumeLoad}
      closePopup={() => setShowPopupResume(false)}
    />
    </>
  )
};

export default ApplicantDetailsTabs;
